import { Popover, PopoverSurface, PopoverTrigger, Select, Title1, shorthands, tokens } from '@fluentui/react-components';
import { t } from 'i18next';
import { ArrowDown, ArrowUp, ArrowUpDown, Check, ChevronLeft, ChevronRight, Filter, PencilLine, ShieldOff, Users, X } from 'lucide-react';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useNotification } from '../../Contexts/NotificationContext';
import { ClientConfiguration, UpdateUsersRoleRequest, UserSummaryItem } from '../../EnterpriseGPTApi/EnterpriseGPTApiV1Client';
import { performEnterpriseGPTApiCall } from '../../Hooks/useMiddlewareApiCall';
import { useFeatures } from '../../store/hooks';
import { makeStyles } from '../../Utils/makeStylesWrapper';
import '../../styles/skeletons.css';
import SyButton from '../Common/SyButton';

const PAGE_SIZE = 10;
const FILTER_DEBOUNCE_MS = 400;

const useStyles = makeStyles({
    page: {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        backgroundColor: tokens.colorNeutralBackground1,
        color: tokens.colorNeutralForeground1,
    },
    header: {
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        height: '24px',
        minHeight: '40px',
        maxHeight: '40px',
        flexShrink: 0,
        backgroundColor: tokens.colorNeutralBackground1,
        borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
        borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
        position: 'sticky',
        top: 0,
        zIndex: 10,
        ...shorthands.padding('10px', '16px'),
    },
    titleGroup: {
        display: 'flex',
        alignItems: 'center',
        ...shorthands.gap(tokens.spacingHorizontalS),
    },
    titleIcon: {
        color: tokens.colorNeutralForeground1,
        flexShrink: 0,
    },
    unauthorized: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        flex: 1,
        gap: tokens.spacingVerticalM,
        color: tokens.colorNeutralForeground3,
    },
    content: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        overflowY: 'auto',
        overflowX: 'hidden',
        minHeight: 0,
        background: tokens.colorNeutralBackground3,
        ...shorthands.padding('12px', '16px', '6px', '16px'),
        ...shorthands.gap('4px'),
        '@media (max-width: 768px)': {
            ...shorthands.padding('16px', '8px', '12px', '8px'),
        },
    },
    toolbar: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        ...shorthands.gap(tokens.spacingHorizontalM),
        flexWrap: 'wrap',
    },
    resultCount: {
        fontSize: tokens.fontSizeBase200,
        color: tokens.colorNeutralForeground3,
        whiteSpace: 'nowrap',
    },
    tableWrapper: {
        overflowX: 'auto',
        borderRadius: tokens.borderRadiusMedium,
        boxShadow: tokens.shadow4,
        backgroundColor: tokens.colorNeutralBackground1,
    },
    table: {
        width: '100%',
        minWidth: '720px',
        tableLayout: 'fixed',
        borderCollapse: 'collapse',
        fontSize: tokens.fontSizeBase300,
    },
    thead: {
        backgroundColor: tokens.colorNeutralBackground2,
        borderBottom: `2px solid ${tokens.colorNeutralStroke2}`,
    },
    th: {
        ...shorthands.padding('12px', '16px'),
        textAlign: 'left',
        fontSize: tokens.fontSizeBase200,
        fontWeight: tokens.fontWeightSemibold,
        color: tokens.colorNeutralForeground3,
        letterSpacing: '0.06em',
        whiteSpace: 'nowrap',
    },
    thContent: {
        display: 'flex',
        alignItems: 'center',
        ...shorthands.gap('4px'),
    },
    thLabel: {
        display: 'inline-flex',
        alignItems: 'center',
        background: 'transparent',
        border: 'none',
        padding: 0,
        cursor: 'pointer',
        font: 'inherit',
        letterSpacing: 'inherit',
        color: 'inherit',
        ':hover': {
            color: tokens.colorNeutralForeground1,
        },
    },
    thName: {
        width: '26%',
    },
    thEmail: {
        width: '29%',
    },
    thRole: {
        width: '13%',
    },
    thLastActive: {
        width: '22%',
        whiteSpace: 'nowrap',
    },
    thActions: {
        textAlign: 'center' as const,
        width: '10%',
    },
    tr: {
        borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
        ':last-child': {
            borderBottom: 'none',
        },
        ':hover': {
            backgroundColor: tokens.colorNeutralBackground1Hover,
        },
    },
    td: {
        ...shorthands.padding('12px', '16px'),
        color: 'var(--color-gray-800)',
        verticalAlign: 'middle',
    },
    tdEllipsis: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    tdCenter: {
        textAlign: 'center' as const,
    },
    emptyRow: {
        ...shorthands.padding('24px', '16px'),
        textAlign: 'center' as const,
        color: tokens.colorNeutralForeground3,
    },
    filterBtn: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '22px',
        height: '22px',
        flexShrink: 0,
        background: 'transparent',
        border: 'none',
        padding: 0,
        borderRadius: tokens.borderRadiusSmall,
        cursor: 'pointer',
        color: tokens.colorNeutralForeground3,
        ':hover': {
            backgroundColor: tokens.colorNeutralBackground1Hover,
            color: tokens.colorNeutralForeground1,
        },
    },
    filterBtnActive: {
        color: tokens.colorBrandForeground1,
    },
    filterPopover: {
        display: 'flex',
        flexDirection: 'column',
        ...shorthands.gap(tokens.spacingVerticalS),
        minWidth: '220px',
    },
    filterLabel: {
        fontSize: tokens.fontSizeBase200,
        color: tokens.colorNeutralForeground3,
    },
    filterInputWrapper: {
        display: 'flex',
        alignItems: 'center',
        ...shorthands.gap(tokens.spacingHorizontalXS),
        height: '32px',
        boxSizing: 'border-box',
        ...shorthands.padding('0', '6px', '0', '8px'),
        ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke1),
        borderRadius: tokens.borderRadiusMedium,
        backgroundColor: tokens.colorNeutralBackground1,
        ':focus-within': {
            ...shorthands.borderColor(tokens.colorCompoundBrandStroke),
        },
    },
    filterInputInner: {
        flex: 1,
        minWidth: 0,
        border: 'none',
        outline: 'none',
        background: 'transparent',
        fontSize: tokens.fontSizeBase300,
        fontFamily: 'inherit',
        color: tokens.colorNeutralForeground1,
    },
    filterClearBtn: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '18px',
        height: '18px',
        flexShrink: 0,
        background: 'transparent',
        border: 'none',
        padding: 0,
        cursor: 'pointer',
        color: tokens.colorNeutralForeground3,
        ':hover': {
            color: tokens.colorNeutralForeground1,
        },
    },
    editCell: {
        display: 'flex',
        alignItems: 'center',
        ...shorthands.gap(tokens.spacingHorizontalXS),
    },
    roleSelect: {
        minWidth: '110px',
        maxWidth: '140px',
    },
    actions: {
        display: 'flex',
        justifyContent: 'center',
        ...shorthands.gap(tokens.spacingHorizontalS),
    },
    editRoleButton: {
        minWidth: '82px',
        whiteSpace: 'nowrap',
        '& .fui-Button__content': {
            whiteSpace: 'nowrap',
        },
    },
    pagination: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        ...shorthands.gap(tokens.spacingHorizontalS),
        ...shorthands.padding('6px', '0'),
        flexWrap: 'wrap',
    },
    pageBtn: {
        minWidth: '36px',
        height: '36px',
        ...shorthands.padding('0', '8px'),
        ...shorthands.border('none', 'solid', 'transparent'),
        borderRadius: '50%',
        background: 'transparent',
        color: 'var(--color-gray-600)',
        cursor: 'pointer',
        fontSize: tokens.fontSizeBase300,
        fontWeight: tokens.fontWeightRegular,
        ':hover': {
            backgroundColor: tokens.colorNeutralBackground1Hover,
            color: 'var(--color-gray-800)',
        },
        ':disabled': {
            opacity: 0.4,
            cursor: 'not-allowed',
        },
    },
    pageBtnActive: {
        backgroundColor: 'var(--color-gray-200)',
        color: 'var(--color-gray-800)',
        fontWeight: tokens.fontWeightSemibold,
        ':hover': {
            backgroundColor: 'var(--color-gray-300)',
        },
    },
    pageBtnNav: {
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        padding: '0 8px',
        height: '36px',
        border: 'none',
        background: 'transparent',
        color: 'var(--color-gray-600)',
        cursor: 'pointer',
        fontSize: tokens.fontSizeBase300,
        borderRadius: tokens.borderRadiusMedium,
        ':hover': {
            color: 'var(--color-gray-800)',
            backgroundColor: tokens.colorNeutralBackground1Hover,
        },
        ':disabled': {
            opacity: 0.4,
            cursor: 'not-allowed',
        },
    },
    ellipsis: {
        display: 'flex',
        alignItems: 'center',
        ...shorthands.padding('0', '4px'),
        color: tokens.colorNeutralForeground3,
    },
    sortIcon: {
        opacity: 0.35,
        display: 'inline-flex',
        verticalAlign: 'middle',
        flexShrink: 0,
        marginLeft: '4px',
    },
    sortIconActive: {
        opacity: 1,
        color: tokens.colorBrandForeground1,
        display: 'inline-flex',
        verticalAlign: 'middle',
        flexShrink: 0,
        marginLeft: '4px',
    },
});

type SortField = 'fullName' | 'email' | 'role' | 'lastActiveAt';
type SortDir = 'asc' | 'desc';

interface Filters {
    fullName: string;
    email: string;
    role: string;
    lastActiveFrom: string;
    lastActiveTo: string;
}

const EMPTY_FILTERS: Filters = { fullName: '', email: '', role: '', lastActiveFrom: '', lastActiveTo: '' };

function formatDateTime(date?: Date): string {
    if (!date) return '—';
    return new Date(date).toLocaleString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
}

function buildPageNumbers(current: number, total: number): (number | '...')[] {
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
    const pages: (number | '...')[] = [1];
    if (current > 3) pages.push('...');
    for (let i = Math.max(2, current - 1); i <= Math.min(total - 1, current + 1); i++) pages.push(i);
    if (current < total - 2) pages.push('...');
    pages.push(total);
    return pages;
}

const UserManagementView: FC = () => {
    const styles = useStyles();
    const { features } = useFeatures();
    const clientConfiguration = features.chat as ClientConfiguration;
    const { dispatchNotification } = useNotification();

    const [users, setUsers] = useState<UserSummaryItem[]>([]);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState<number | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [sortField, setSortField] = useState<SortField>('fullName');
    const [sortDir, setSortDir] = useState<SortDir>('asc');
    const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
    const [availableRoles, setAvailableRoles] = useState<string[]>([]);
    const [editingEmail, setEditingEmail] = useState<string | null>(null);
    const [editingRole, setEditingRole] = useState<string>('');
    const [isSaving, setIsSaving] = useState(false);
    const [openFilterPopover, setOpenFilterPopover] = useState<string | null>(null);
    const filtersInitialized = useRef(false);

    const isAdmin = clientConfiguration?.isAdmin === true;

    const loadPage = useCallback(
        async (targetPage: number, field: SortField, dir: SortDir, activeFilters: Filters, abortController?: AbortController) => {
            setIsLoading(true);
            await performEnterpriseGPTApiCall(async (client, signal) => {
                try {
                    const result = await client.getUsers(
                        targetPage,
                        PAGE_SIZE,
                        field,
                        dir,
                        activeFilters.fullName || undefined,
                        activeFilters.email || undefined,
                        activeFilters.role || undefined,
                        activeFilters.lastActiveFrom ? new Date(`${activeFilters.lastActiveFrom}T00:00:00`) : undefined,
                        activeFilters.lastActiveTo ? new Date(`${activeFilters.lastActiveTo}T23:59:59`) : undefined,
                        true,
                        signal,
                    );

                    setUsers(result.items ?? []);
                    setTotalPages(result.totalPages && result.totalPages > 0 ? result.totalPages : 1);
                    setTotalCount(result.totalCount ?? null);
                    setPage(targetPage);
                } catch (e: unknown) {
                    if ((e as Error).message !== 'canceled') {
                        dispatchNotification(t('adminSettings.rbac.loadFailed', 'Unable to load the users'), 'error');
                    }
                } finally {
                    setIsLoading(false);
                }
            }, abortController ? { abortController } : undefined);
        },
        [dispatchNotification],
    );

    useEffect(() => {
        if (!isAdmin) {
            setIsLoading(false);
            return;
        }

        const pageAbortController = new AbortController();
        const rolesAbortController = new AbortController();

        loadPage(1, sortField, sortDir, EMPTY_FILTERS, pageAbortController);

        performEnterpriseGPTApiCall(async (client) => {
            try {
                const roles = await client.getAllRoleNames();
                setAvailableRoles(Array.isArray(roles) ? roles.slice().sort() : []);
            } catch {
                setAvailableRoles([]);
            }
        }, { abortController: rolesAbortController });

        return () => {
            pageAbortController.abort();
            rolesAbortController.abort();
        };
    }, [isAdmin]);   // eslint-disable-line react-hooks/exhaustive-deps

    // Reset the guard only on unmount so React StrictMode's remount doesn't bypass it.
    useEffect(() => () => { filtersInitialized.current = false; }, []);

    // Text filters are debounced so that typing does not trigger a request per keystroke.
    useEffect(() => {
        if (!isAdmin) return;
        if (!filtersInitialized.current) {
            filtersInitialized.current = true;
            return;
        }

        const timer = setTimeout(() => {
            loadPage(1, sortField, sortDir, filters);
        }, FILTER_DEBOUNCE_MS);

        return () => clearTimeout(timer);
    }, [filters]);   // eslint-disable-line react-hooks/exhaustive-deps

    const handleSort = (field: SortField) => {
        const newDir: SortDir = sortField === field && sortDir === 'asc' ? 'desc' : 'asc';
        setSortField(field);
        setSortDir(newDir);
        cancelEdit();
        loadPage(1, field, newDir, filters);
    };

    const handlePageChange = (targetPage: number) => {
        if (targetPage < 1 || targetPage > totalPages || targetPage === page) return;
        cancelEdit();
        loadPage(targetPage, sortField, sortDir, filters);
    };

    const updateFilter = (key: keyof Filters, value: string) => {
        setFilters(prev => ({ ...prev, [key]: value }));
    };

    const startEdit = (user: UserSummaryItem) => {
        setEditingEmail(user.email ?? null);
        setEditingRole(user.role ?? '');
    };

    const cancelEdit = () => {
        setEditingEmail(null);
        setEditingRole('');
    };

    const saveRole = async (user: UserSummaryItem) => {
        if (!user.email || !editingRole || editingRole === user.role) {
            cancelEdit();
            return;
        }

        const previousRole = user.role;
        setIsSaving(true);
        setUsers(prev => prev.map(u => (u.email === user.email ? Object.assign(u.clone(), { role: editingRole }) : u)));

        try {
            await performEnterpriseGPTApiCall((client) =>
                client.updateUsersRole(new UpdateUsersRoleRequest({ users: [user.email!], role: editingRole })),
            );
            dispatchNotification(t('adminSettings.rbac.roleUpdated', 'The user role has been updated'), 'success');
            cancelEdit();
        } catch {
            setUsers(prev => prev.map(u => (u.email === user.email ? Object.assign(u.clone(), { role: previousRole }) : u)));
            dispatchNotification(t('adminSettings.rbac.updateFailed', 'Failed to update user role'), 'error');
        } finally {
            setIsSaving(false);
        }
    };

    const sortIndicator = (field: SortField) =>
        sortField === field
            ? (sortDir === 'asc'
                ? <ArrowUp size={12} className={styles.sortIconActive} />
                : <ArrowDown size={12} className={styles.sortIconActive} />)
            : <ArrowUpDown size={12} className={styles.sortIcon} />;

    const renderTextFilter = (key: 'fullName' | 'email', label: string) => (
        <Popover
            trapFocus
            open={openFilterPopover === key}
            onOpenChange={(_, data) => setOpenFilterPopover(data.open ? key : null)}
        >
            <PopoverTrigger disableButtonEnhancement>
                <button
                    className={`${styles.filterBtn} ${filters[key] ? styles.filterBtnActive : ''}`}
                    aria-label={label}
                    title={label}
                >
                    <Filter size={13} />
                </button>
            </PopoverTrigger>
            <PopoverSurface>
                <div className={styles.filterPopover}>
                    <label className={styles.filterLabel} htmlFor={`filter-${key}`}>{label}</label>
                    <div className={styles.filterInputWrapper}>
                        <input
                            id={`filter-${key}`}
                            className={styles.filterInputInner}
                            type='text'
                            value={filters[key]}
                            onChange={e => updateFilter(key, e.target.value)}
                            onKeyDown={e => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    setOpenFilterPopover(null);
                                }
                            }}
                            autoFocus
                        />
                        {filters[key] && (
                            <button
                                type='button'
                                className={styles.filterClearBtn}
                                onClick={() => updateFilter(key, '')}
                                aria-label={t('adminSettings.rbac.clearFilter', 'Clear')}
                                title={t('adminSettings.rbac.clearFilter', 'Clear')}
                            >
                                <X size={14} />
                            </button>
                        )}
                    </div>
                </div>
            </PopoverSurface>
        </Popover>
    );

    const renderRoleFilter = () => (
        <Popover
            trapFocus
            open={openFilterPopover === 'role'}
            onOpenChange={(_, data) => setOpenFilterPopover(data.open ? 'role' : null)}
        >
            <PopoverTrigger disableButtonEnhancement>
                <button
                    className={`${styles.filterBtn} ${filters.role ? styles.filterBtnActive : ''}`}
                    aria-label={t('adminSettings.rbac.filterRole', 'Filter Role')}
                    title={t('adminSettings.rbac.filterRole', 'Filter Role')}
                >
                    <Filter size={13} />
                </button>
            </PopoverTrigger>
            <PopoverSurface>
                <div className={styles.filterPopover}>
                    <span className={styles.filterLabel}>{t('adminSettings.rbac.filterRole', 'Filter Role')}</span>
                    <Select
                        size='small'
                        value={filters.role}
                        onChange={(_, data) => updateFilter('role', data.value)}
                        aria-label={t('adminSettings.rbac.filterRole', 'Filter Role')}
                        className={styles.roleSelect}
                    >
                        <option value=''>{t('adminSettings.rbac.allRoles', 'All roles')}</option>
                        {availableRoles.map(role => (
                            <option key={role} value={role}>{role}</option>
                        ))}
                    </Select>
                </div>
            </PopoverSurface>
        </Popover>
    );

    const renderLastActiveFilter = () => {
        const isActive = Boolean(filters.lastActiveFrom || filters.lastActiveTo);
        return (
            <Popover
                trapFocus
                open={openFilterPopover === 'lastActive'}
                onOpenChange={(_, data) => setOpenFilterPopover(data.open ? 'lastActive' : null)}
            >
                <PopoverTrigger disableButtonEnhancement>
                    <button
                        className={`${styles.filterBtn} ${isActive ? styles.filterBtnActive : ''}`}
                        aria-label={t('adminSettings.rbac.filterLastActive', 'Filter Last Active')}
                        title={t('adminSettings.rbac.filterLastActive', 'Filter Last Active')}
                    >
                        <Filter size={13} />
                    </button>
                </PopoverTrigger>
                <PopoverSurface>
                    <div className={styles.filterPopover}>
                        <label className={styles.filterLabel} htmlFor='filter-last-active-from'>
                            {t('adminSettings.rbac.lastActiveFrom', 'From')}
                        </label>
                        <div className={styles.filterInputWrapper}>
                            <input
                                id='filter-last-active-from'
                                className={styles.filterInputInner}
                                type='date'
                                value={filters.lastActiveFrom}
                                max={filters.lastActiveTo || undefined}
                                onChange={e => updateFilter('lastActiveFrom', e.target.value)}
                                onKeyDown={e => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        setOpenFilterPopover(null);
                                    }
                                }}
                            />
                            {filters.lastActiveFrom && (
                                <button
                                    type='button'
                                    className={styles.filterClearBtn}
                                    onClick={() => updateFilter('lastActiveFrom', '')}
                                    aria-label={t('adminSettings.rbac.clearFilter', 'Clear')}
                                    title={t('adminSettings.rbac.clearFilter', 'Clear')}
                                >
                                    <X size={14} />
                                </button>
                            )}
                        </div>
                        <label className={styles.filterLabel} htmlFor='filter-last-active-to'>
                            {t('adminSettings.rbac.lastActiveTo', 'To')}
                        </label>
                        <div className={styles.filterInputWrapper}>
                            <input
                                id='filter-last-active-to'
                                className={styles.filterInputInner}
                                type='date'
                                value={filters.lastActiveTo}
                                min={filters.lastActiveFrom || undefined}
                                onChange={e => updateFilter('lastActiveTo', e.target.value)}
                                onKeyDown={e => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        setOpenFilterPopover(null);
                                    }
                                }}
                            />
                            {filters.lastActiveTo && (
                                <button
                                    type='button'
                                    className={styles.filterClearBtn}
                                    onClick={() => updateFilter('lastActiveTo', '')}
                                    aria-label={t('adminSettings.rbac.clearFilter', 'Clear')}
                                    title={t('adminSettings.rbac.clearFilter', 'Clear')}
                                >
                                    <X size={14} />
                                </button>
                            )}
                        </div>
                    </div>
                </PopoverSurface>
            </Popover>
        );
    };

    const renderSkeletonRows = () =>
        Array.from({ length: PAGE_SIZE }).map((_, i) => (
            <tr key={i} className={styles.tr}>
                <td className={styles.td}><Skeleton width='70%' /></td>
                <td className={styles.td}><Skeleton width='80%' /></td>
                <td className={styles.td}><Skeleton width={70} /></td>
                <td className={styles.td}><Skeleton width={120} /></td>
                <td className={styles.td}><Skeleton width={60} /></td>
            </tr>
        ));

    if (!isAdmin) {
        return (
            <div className={styles.page}>
                <div className={styles.header}>
                    <div className={styles.titleGroup}>
                        <Users size={20} className={styles.titleIcon} />
                        <Title1 className='settingsTitle'>{t('adminSettings.rbac.title', 'User Management')}</Title1>
                    </div>
                </div>
                <div className={styles.unauthorized}>
                    <ShieldOff size={32} />
                    <span>{t('adminSettings.forbiddenPageAccess', 'You do not have access to this page')}</span>
                </div>
            </div>
        );
    }

    const hasFilters = Boolean(filters.fullName || filters.email || filters.role || filters.lastActiveFrom || filters.lastActiveTo);

    return (
        <div className={styles.page}>
            <div className={styles.header}>
                <div className={styles.titleGroup}>
                    <Users size={20} className={styles.titleIcon} />
                    <Title1 className='settingsTitle'>{t('adminSettings.rbac.title', 'User Management')}</Title1>
                </div>
            </div>

            <div className={styles.content}>
                <div className={styles.toolbar}>
                    {/* <span className={styles.resultCount}>
                        {totalCount !== null && !isLoading
                            ? t('adminSettings.rbac.results', 'Results: {{count}}', { count: totalCount })
                            : ''}
                    </span> */}
                    {hasFilters && (
                        <SyButton appearance='outline3' size='extra-small' fontSize='13px' onClick={() => setFilters(EMPTY_FILTERS)}>
                            {t('adminSettings.rbac.clearFilters', 'Clear filters')}
                        </SyButton>
                    )}
                </div>

                <div className={styles.tableWrapper}>
                    <table className={styles.table}>
                        <thead className={styles.thead}>
                            <tr>
                                <th
                                    className={`${styles.th} ${styles.thName}`}
                                    aria-sort={sortField === 'fullName' ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
                                >
                                    <div className={styles.thContent}>
                                        <button className={styles.thLabel} onClick={() => handleSort('fullName')}>
                                            {t('adminSettings.rbac.col.fullName', 'FULL NAME')}
                                            {sortIndicator('fullName')}
                                        </button>
                                        {renderTextFilter('fullName', t('adminSettings.rbac.filterFullName', 'Filter Full Name'))}
                                    </div>
                                </th>
                                <th
                                    className={`${styles.th} ${styles.thEmail}`}
                                    aria-sort={sortField === 'email' ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
                                >
                                    <div className={styles.thContent}>
                                        <button className={styles.thLabel} onClick={() => handleSort('email')}>
                                            {t('adminSettings.rbac.col.email', 'EMAIL')}
                                            {sortIndicator('email')}
                                        </button>
                                        {renderTextFilter('email', t('adminSettings.rbac.filterEmail', 'Filter Email'))}
                                    </div>
                                </th>
                                <th
                                    className={`${styles.th} ${styles.thRole}`}
                                    aria-sort={sortField === 'role' ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
                                >
                                    <div className={styles.thContent}>
                                        <button className={styles.thLabel} onClick={() => handleSort('role')}>
                                            {t('adminSettings.rbac.col.role', 'ROLE')}
                                            {sortIndicator('role')}
                                        </button>
                                        {renderRoleFilter()}
                                    </div>
                                </th>
                                <th
                                    className={`${styles.th} ${styles.thLastActive}`}
                                    aria-sort={sortField === 'lastActiveAt' ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
                                >
                                    <div className={styles.thContent}>
                                        <button className={styles.thLabel} onClick={() => handleSort('lastActiveAt')}>
                                            {t('adminSettings.rbac.col.lastActive', 'LAST ACTIVE')}
                                            {sortIndicator('lastActiveAt')}
                                        </button>
                                        {renderLastActiveFilter()}
                                    </div>
                                </th>
                                <th className={`${styles.th} ${styles.thActions}`}>
                                    {t('adminSettings.rbac.col.actions', 'ACTIONS')}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {isLoading
                                ? renderSkeletonRows()
                                : users.length === 0
                                    ? (
                                        <tr>
                                            <td className={styles.emptyRow} colSpan={5}>
                                                {t('adminSettings.rbac.noData', 'No data available')}
                                            </td>
                                        </tr>
                                    )
                                    : users.map(user => {
                                        const isEditing = editingEmail === user.email;
                                        return (
                                            <tr key={user.email} className={styles.tr}>
                                                <td className={`${styles.td} ${styles.tdEllipsis}`} title={user.fullName ?? undefined}>
                                                    {user.fullName || '—'}
                                                </td>
                                                <td className={`${styles.td} ${styles.tdEllipsis}`} title={user.email ?? undefined}>
                                                    {user.email}
                                                </td>
                                                <td className={styles.td}>
                                                    {isEditing ? (
                                                        <div className={styles.editCell}>
                                                            <Select
                                                                size='small'
                                                                value={editingRole}
                                                                onChange={(_, data) => setEditingRole(data.value)}
                                                                disabled={isSaving}
                                                                aria-label={t('adminSettings.rbac.selectRole', 'Select role')}
                                                                className={styles.roleSelect}
                                                            >
                                                                {availableRoles.map(role => (
                                                                    <option key={role} value={role}>{role}</option>
                                                                ))}
                                                            </Select>
                                                        </div>
                                                    ) : (
                                                        user.role || '—'
                                                    )}
                                                </td>
                                                <td className={styles.td}>{formatDateTime(user.lastActiveAt)}</td>
                                                <td className={`${styles.td} ${styles.tdCenter}`}>
                                                    <div className={styles.actions}>
                                                        {isEditing ? (
                                                            <>
                                                                <SyButton
                                                                    appearance='secondary'
                                                                    size='extra-small'
                                                                    fontSize='13px'
                                                                    icon={<X size={14} />}
                                                                    iconOnly
                                                                    disabled={isSaving}
                                                                    onClick={cancelEdit}
                                                                    aria-label={t('adminSettings.rbac.cancel', 'Cancel changes')}
                                                                    title={t('adminSettings.rbac.cancel', 'Cancel changes')}
                                                                />
                                                                <SyButton
                                                                    appearance='secondary'
                                                                    size='extra-small'
                                                                    fontSize='13px'
                                                                    icon={<Check size={14} />}
                                                                    iconOnly
                                                                    disabled={isSaving}
                                                                    onClick={() => saveRole(user)}
                                                                    aria-label={t('adminSettings.rbac.save', 'Save changes')}
                                                                    title={t('adminSettings.rbac.save', 'Save changes')}
                                                                />
                                                            </>
                                                        ) : (
                                                            <SyButton
                                                                appearance='outline3'
                                                                size='extra-small'
                                                                fontSize='13px'
                                                                icon={<PencilLine size={14} />}
                                                                className={styles.editRoleButton}
                                                                disabled={editingEmail !== null}
                                                                onClick={() => startEdit(user)}
                                                            >
                                                                {t('adminSettings.rbac.editRole', 'Edit Role')}
                                                            </SyButton>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                        </tbody>
                    </table>
                </div>

                {!isLoading && totalPages > 1 && (
                    <nav className={styles.pagination} aria-label='Pagination'>
                        <button
                            className={styles.pageBtnNav}
                            onClick={() => handlePageChange(page - 1)}
                            disabled={page === 1}
                            aria-label={t('adminSettings.rbac.pagination.previous', 'Previous')}
                        >
                            <ChevronLeft size={14} />
                            {t('adminSettings.rbac.pagination.previous', 'Previous')}
                        </button>

                        {buildPageNumbers(page, totalPages).map((p, i) =>
                            p === '...' ? (
                                <span key={`ellipsis-${i}`} className={styles.ellipsis}>…</span>
                            ) : (
                                <button
                                    key={p}
                                    className={`${styles.pageBtn} ${p === page ? styles.pageBtnActive : ''}`}
                                    onClick={() => handlePageChange(p as number)}
                                    aria-label={`Page ${p}`}
                                    aria-current={p === page ? 'page' : undefined}
                                >
                                    {p}
                                </button>
                            ),
                        )}

                        <button
                            className={styles.pageBtnNav}
                            onClick={() => handlePageChange(page + 1)}
                            disabled={page === totalPages}
                            aria-label={t('adminSettings.rbac.pagination.next', 'Next')}
                        >
                            {t('adminSettings.rbac.pagination.next', 'Next')}
                            <ChevronRight size={14} />
                        </button>
                    </nav>
                )}
            </div>
        </div>
    );
};

export default UserManagementView;
